import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { API_CONFIG, AUTH_CONFIG } from '@/utils/constants';

// Types for API responses
export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  success: boolean;
}

export interface ApiErrorResponse {
  message: string;
  errors?: Record<string, string[]>;
  code?: string;
  statusCode: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

// Custom error class for API errors
export class ApiError extends Error {
  public statusCode: number;
  public code?: string;
  public errors?: Record<string, string[]>;

  constructor(response: ApiErrorResponse) {
    super(response.message);
    this.name = 'ApiError';
    this.statusCode = response.statusCode;
    this.code = response.code;
    this.errors = response.errors;
  }
}

// Token storage utilities
const tokenStorage = {
  getAccessToken: (): string | null => {
    return localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem(AUTH_CONFIG.REFRESH_TOKEN_KEY);
  },

  setTokens: (accessToken: string, refreshToken: string): void => {
    localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, accessToken);
    localStorage.setItem(AUTH_CONFIG.REFRESH_TOKEN_KEY, refreshToken);
  },

  clearTokens: (): void => {
    localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
    localStorage.removeItem(AUTH_CONFIG.REFRESH_TOKEN_KEY);
    localStorage.removeItem(AUTH_CONFIG.USER_KEY);
  },
};

// Create axios instance
const createApiInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_CONFIG.BASE_URL,
    timeout: API_CONFIG.TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  // Request interceptor
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = tokenStorage.getAccessToken();

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add request timestamp for debugging
      config.metadata = { startTime: new Date() };

      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      // Log response time in development
      if (import.meta.env.DEV && response.config.metadata) {
        const endTime = new Date();
        const duration = endTime.getTime() - response.config.metadata.startTime.getTime();
        console.debug(`[API] ${response.config.method?.toUpperCase()} ${response.config.url} - ${duration}ms`);
      }

      return response;
    },
    async (error: AxiosError<ApiErrorResponse>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Handle 401 Unauthorized - attempt token refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const refreshToken = tokenStorage.getRefreshToken();

          if (refreshToken) {
            const response = await axios.post<{ success: boolean; data: {
              accessToken: string;
              refreshToken: string;
            }; }>(`${API_CONFIG.BASE_URL}/auth/refresh-token`, {
              refreshToken,
            });

            const { accessToken, refreshToken: newRefreshToken } = response.data.data;
            tokenStorage.setTokens(accessToken, newRefreshToken);

            // Retry original request with new token
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            }

            return instance(originalRequest);
          }
        } catch (refreshError) {
          // Refresh failed - clear tokens and redirect to login
          tokenStorage.clearTokens();
          window.location.href = '/login?session=expired';
          return Promise.reject(refreshError);
        }
      }

      // Handle other errors
      const apiError = new ApiError({
        message: error.response?.data?.message || error.message || 'An unexpected error occurred',
        statusCode: error.response?.status || 500,
        code: error.response?.data?.code,
        errors: error.response?.data?.errors,
      });

      // Log errors in development
      if (import.meta.env.DEV) {
        console.error('[API Error]', {
          url: error.config?.url,
          status: error.response?.status,
          message: apiError.message,
          errors: apiError.errors,
        });
      }

      return Promise.reject(apiError);
    }
  );

  return instance;
};

// Export the configured API instance
export const api = createApiInstance();

// Export token storage utilities
export { tokenStorage };

// Generic request helpers
export const apiHelpers = {
  /**
   * GET request helper
   */
  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await api.get<ApiResponse<T>>(url, { params });
    return response.data.data;
  },

  /**
   * POST request helper
   */
  async post<T>(url: string, data?: unknown): Promise<T> {
    const response = await api.post<ApiResponse<T>>(url, data);
    return response.data.data;
  },

  /**
   * PUT request helper
   */
  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await api.put<ApiResponse<T>>(url, data);
    return response.data.data;
  },

  /**
   * PATCH request helper
   */
  async patch<T>(url: string, data?: unknown): Promise<T> {
    const response = await api.patch<ApiResponse<T>>(url, data);
    return response.data.data;
  },

  /**
   * DELETE request helper
   */
  async delete<T>(url: string, data?: unknown): Promise<T> {
    const response = await api.delete<ApiResponse<T>>(url, { data });
    return response.data.data;
  },

  /**
   * Upload file helper
   */
  async upload<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<ApiResponse<T>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data.data;
  },

  /**
   * Download file helper
   */
  async download(url: string, filename: string): Promise<void> {
    const response = await api.get(url, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data as BlobPart]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  },
};

// Declare module augmentation for axios config
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    metadata?: {
      startTime: Date;
    };
  }
}

export default api;
